import _ from "lodash";
import { moreProductsInfoList } from "../MoreApplications/config";
import { ourProductsInfoList } from "../OurProducts/config";
import {
  appInfoPhoneFirstList,
  appInfoPhoneSecondList,
} from "../LaiYinSeries/config";
import { appInfoList as lingganCardApps } from "../LingGanSeries/config";
import appRegistry from "./apps.json";
import { appFeatureFallback } from "./features";

/**
 * App 详情页的数据装配层。
 *
 * 一个页面三处来源：
 * 1. store-data/<slug>.json —— 由 scripts/fetch-app-store-meta.js 从 App Store 接口固化下来的商店元数据
 *    （名称、评分、截图、简介、支持系统…），构建时只读本地文件，运行时不依赖 Apple 接口；
 * 2. OurProducts / MoreApplications 的 config —— 官网已有的下载入口与兜底功能文案；
 * 3. LaiYinSeries / LingGanSeries 的 config —— 产品卡上的图标、一句话简介与应用市场奖项。
 *
 * 新增一个 App：在 apps.json 登记 slug 与 trackId，跑一次 `node scripts/fetch-app-store-meta.js`
 * 生成 store-data/<slug>.json 即可，页面会自动出现在路由、sitemap 与产品卡链接里。
 */
const SERIES = {
  来音: { pathname: "/laiyinSeries", anchor: "/#laiyin-series", label: "来音系列" },
  灵敢: { pathname: "/lingganSeries", anchor: "/#linggan-series", label: "灵敢系列" },
};

// 两个品牌线各自的产品卡与详情文案来源
const BRAND_SOURCES = {
  来音: {
    cards: [...appInfoPhoneFirstList, ...appInfoPhoneSecondList],
    details: ourProductsInfoList,
  },
  灵敢: {
    cards: lingganCardApps,
    details: moreProductsInfoList,
  },
};

// 构建时把 store-data 下的 JSON 全部收进来，新增文件不需要再改 import
const storeFiles = require.context("./store-data", false, /\.json$/);

const loadStore = (slug) => {
  const key = `./${slug}.json`;
  return _.includes(storeFiles.keys(), key) ? storeFiles(key) : null;
};

/**
 * 商店简介里的合规样板：会员协议 / 用户协议 / 隐私政策、分隔线、小标题装饰、客服邮箱等。
 * 这些是 Apple 上架要求，放在官网正文里既难看又和 App Store 的排版对不上，统一剔除。
 */
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[a-zA-Z]{2,}/;
const isBoilerplateLine = (line) =>
  !line ||
  /^[-—=*_·\s]+$/.test(line) || // 纯分隔线
  /^[-—=~*_·\s]*[\u4e00-\u9fa5]{2,6}[-—=~*_·\s]*$/.test(line) || // “------核心功能------”这类装饰小标题
  /https?:\/\//.test(line) || // 协议链接
  /(会员协议|用户协议|隐私政策|隐私协议|服务协议|自动续费)/.test(line) ||
  /(客服|反馈|联系我们|咨询).{0,12}(邮箱|邮件|@)/.test(line) ||
  /欢迎.{0,12}(反馈|联系|交流|沟通)/.test(line) || // “有任何问题欢迎通过邮件向我们反馈”
  EMAIL_RE.test(line);

/** 功能说明结尾常带分号，卡片式排版里读起来像半句话，统一去掉 */
const trimFeatureInfo = (info) => info.replace(/[\s；;，,、]+$/, "");

/**
 * 把商店简介拆成「功能亮点 + 正文段落」。
 * App Store 的简介普遍写成【小标题】+ 说明，这里按同样的口径解析出来，
 * 保证官网列的功能点与商店一字不差，避免两处说法不一致被引擎判为冲突信息。
 */
const parseStoreDescription = (description) => {
  const lines = _.chain(description || "")
    .split("\n")
    .map((line) => line.trim())
    .reject(isBoilerplateLine)
    .value();
  const titleOnlyRe = /^【([^】]{2,24})】\s*$/;
  const consumed = new Set();
  const features = [];
  lines.forEach((line, index) => {
    const matched = line.match(/^【([^】]{2,24})】\s*(.*)$/);
    if (!matched) return;
    let info = matched[2];
    if (!info) {
      // 小标题单独占一行，说明跟在下一行
      const next = lines[index + 1] || "";
      if (next && !titleOnlyRe.test(next)) {
        info = next;
        consumed.add(index + 1);
      }
    }
    if (!info) return;
    consumed.add(index);
    features.push({ title: matched[1], info: trimFeatureInfo(info) });
  });
  const paragraphs = lines.filter(
    (line, index) => !consumed.has(index) && !titleOnlyRe.test(line)
  );
  return { features, paragraphs };
};

const buildPage = (app) => {
  const { slug, appName, brand, detailName } = app;
  const sources = _.get(BRAND_SOURCES, brand, { cards: [], details: [] });
  // 官网里的叫法和商店名未必一致（乐理手册 ↔ 来音乐理、灵敢文案 ↔ 灵敢文案提取），
  // 详情文案按 detailName 兜一次；产品卡仍以官网展示名为主
  const lookupName = detailName || appName;
  const card =
    _.find(sources.cards, { appName }) ||
    _.find(sources.cards, { appName: lookupName });
  const detail =
    _.find(sources.details, { appName: lookupName }) ||
    _.find(sources.details, { appName });
  const store = loadStore(slug);
  if (!store) return null;
  const parsed = parseStoreDescription(store.description);
  // 官网产品弹层自己写的分条文案（只有 5 个 App 有）与 features.js 的补充文案
  const websiteFeatures = _.isEmpty(_.get(detail, "infos"))
    ? _.get(appFeatureFallback, slug, [])
    : detail.infos;
  // 商店简介里能解析出成体系的【功能】时以商店为准（页面与 App Store 口径一致），
  // 解析不出来（如来音小提琴）才退回官网文案
  const features =
    _.size(parsed.features) >= 3 ? parsed.features : websiteFeatures;

  return {
    slug,
    appName,
    aliasName: lookupName,
    brand,
    series: SERIES[brand],
    // URL 必须带 .html：线上静态服务（共享 fe/static-server 里的 Express）只对
    // 精确文件路径放行，无扩展名路径会被 history fallback 全打成根 index.html，
    // 目录式产物 app/<slug>/index.html 也无法通过 /app/<slug> 访问。
    // 客户端路由仍是 /app/:slug，:slug 拿到的就是 "<slug>.html"，与这里的 pathname 同口径。
    pathname: `/app/${slug}.html`,
    filename: `app/${slug}.html`,
    store,
    card,
    // 下载入口：iOS 用商店链接，Android 用应用市场 scheme
    iosLink: _.get(detail, "iOSDownloadLink", _.get(store, "appStoreUrl")),
    androidMarket: _.get(detail, "androidMarket", ""),
    androidPackage: _.get(detail, "androidMarket", "").replace(
      "market://details?id=",
      ""
    ),
    // 功能亮点：优先用商店简介里的【…】分条（与 App Store 完全一致），
    // 解析不出成体系分条时才退回官网文案
    features,
    // 简介正文：商店 description 去掉协议等样板后的段落
    paragraphs: parsed.paragraphs,
    awards: _.get(card, "awards", []),
    icon: _.get(card, "appIcon", _.get(store, "iconUrl")),
    subtitle: _.get(card, "appIntro", "").trim(),
  };
};

// 没有 store-data 的 App 不生成页面，产品卡上也就不会出现链接
export const appDetailPages = _.compact(_.map(appRegistry, buildPage));

export const findAppDetailByPathname = (pathname) =>
  _.find(appDetailPages, (page) => page.pathname === pathname);

/**
 * 按 App 名称查它的详情页（产品卡用它决定是否链过去）。
 * 只有配了 store-data 的 App 才有静态详情页，查不到就返回 undefined，调用处不渲染链接。
 */
export const findAppDetailByAppName = (appName) =>
  _.find(
    appDetailPages,
    (page) => page.appName === appName || page.aliasName === appName
  );

/**
 * 评分人数：225586 -> "22.6万"
 */
export const formatRatingCount = (count) => {
  if (!count) return "";
  return count >= 10000 ? `${(count / 10000).toFixed(1)}万` : `${count}`;
};
